import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building, Plus, Trash2, Check } from "lucide-react";

const Row = ({ d, onRename, onDelete }) => {
  const [name, setName] = useState(d.name);
  useEffect(() => setName(d.name), [d.name]);
  return (
    <li className="flex items-center gap-2 py-1.5" data-testid={`dept-row-${d.id}`}>
      <Input data-testid={`dept-name-${d.id}`} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} className="h-8" />
      {name.trim() && name.trim() !== d.name && (
        <Button size="icon" variant="ghost" data-testid={`dept-save-${d.id}`} onClick={() => onRename(d, name)} className="h-8 w-8 text-blue-700"><Check className="w-4 h-4" /></Button>
      )}
      <Button size="icon" variant="ghost" data-testid={`dept-delete-${d.id}`} onClick={() => onDelete(d)} className="h-8 w-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
    </li>
  );
};

export const DepartmentsManager = () => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/departments").then((r) => setItems(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/departments", { name }); setName(""); load(); toast.success("Department added"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const rename = async (d, n) => {
    try { await api.patch(`/departments/${d.id}`, { name: n }); load(); toast.success("Department renamed"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete department “${d.name}”? People in it will have no department until you pick a new one.`)) return;
    try { const r = await api.delete(`/departments/${d.id}`); load(); toast.success(`Deleted · ${r.data.users_updated} user(s) unassigned`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="departments-manager">
      <div className="flex items-center gap-2 mb-1"><Building className="w-4 h-4 text-blue-700" /><h2 className="text-sm font-semibold">Departments</h2></div>
      <p className="text-xs text-slate-500 mb-3">Top Management users manage everyone in their departments. Assign departments under Admin → Users & invites.</p>
      <div className="flex gap-2">
        <Input data-testid="dept-new-name" value={name} maxLength={60} placeholder="e.g. Accounts" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="h-9" />
        <Button data-testid="dept-add" onClick={add} className="bg-blue-700 hover:bg-blue-800 text-white gap-1 h-9"><Plus className="w-4 h-4" />Add</Button>
      </div>
      <ul className="mt-3 divide-y divide-slate-100">
        {items.length === 0 && <li className="text-xs text-slate-400 py-2" data-testid="dept-empty">No departments yet.</li>}
        {items.map((d) => <Row key={d.id} d={d} onRename={rename} onDelete={remove} />)}
      </ul>
    </section>
  );
};
